import { gql } from '@apollo/client';
import { Box, Button, Typography } from '@mui/material';
import { ContextMenu } from 'components/common/ContextMenu';
import { CheckboxMenuItem } from 'components/common/form/Select';
import { IconLinearArrowDown } from 'components/icons/components/linear/IconLinearArrowDown';
import { IconLinearDollarCircle } from 'components/icons/components/linear/IconLinearDollarCircle';
import {
  SimpleDateRangeFilterInput,
  SocialPostMentionSource,
  useGetBrandMentionStatsGraphForSlBrandMentionChartQuery,
} from 'graphql/generated';
import moment from 'moment';
import { useMemo, useState } from 'react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
  YAxis,
} from 'recharts';
import { theme } from 'styles/theme';
import { CreatePortalById } from 'utils/helpers';
import { formatBigNumber } from 'utils/number';

const STATIC_POINTS = Array.from({ length: 6 }, (_, index) => ({
  date: moment().subtract(index, 'days').format('YYYY-MM-DD'),
  brandMentionCount: 0,
}));

const Y_AXIS_TICKS = [0, 100, 200, 300, 400];

const createCustomTooltipContent = () => {
  return function CustomTooltipContent({
    active,
    payload,
  }: TooltipProps<number, string>) {
    if (active && payload && payload.length) {
      const data = payload[0].payload;

      return (
        <Box
          sx={{
            backgroundColor: theme.colors?.primary.black,
            p: theme.spacing(1.5, 3),
            borderRadius: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Typography
            variant="headline-xs"
            color={theme.colors?.primary.parchment}
          >
            {moment(data.date).format('DD MMM YYYY')}
          </Typography>
          <Typography
            variant="headline-sm"
            color={theme.colors?.primary.parchment}
          >
            {formatBigNumber(data.brandMentionCount)} mentions
          </Typography>
        </Box>
      );
    }
    return null;
  };
};

// eslint-disable-next-line
gql`
  query GetBrandMentionStatsGraphForSLBrandMentionChart(
    $input: CreatorProfileBrandMentionStatsInput!
  ) {
    creatorProfileBrandMentionStatsGraph(input: $input) {
      totalCount
      previousDurationTotalCount
      data {
        date
        brandMentionCount
      }
    }
  }
`;

interface Props {
  brandId: string;
  selectedDateRange: SimpleDateRangeFilterInput;
  onClickChartPoint: () => void;
}

export const SocialListeningBrandMentionChart = ({
  brandId,
  selectedDateRange,
  onClickChartPoint,
}: Props) => {
  const [selectedSource, setSelectedSource] = useState<
    SocialPostMentionSource[]
  >([]);
  const { data, loading } =
    useGetBrandMentionStatsGraphForSlBrandMentionChartQuery({
      variables: {
        input: {
          dateRange: selectedDateRange,
          brandId,
          sources: selectedSource,
        },
      },
    });

  const brandMentionData = useMemo(() => {
    return data?.creatorProfileBrandMentionStatsGraph;
  }, [data]);

  const hasData = brandMentionData?.data && brandMentionData.data.length > 0;

  const onSelectSource = (source: SocialPostMentionSource) => {
    setSelectedSource((prev) =>
      prev.includes(source)
        ? prev.filter((s) => s !== source)
        : [...prev, source],
    );
  };

  return (
    <>
      <CreatePortalById containerId="brand-mention-dialog-filters">
        <ContextMenu
          renderButton={() => (
            <Button
              variant="tertiary"
              startIcon={
                <Box
                  sx={{
                    borderRadius: 1,
                    background: 'rgba(35, 6, 3, 0.05)',
                    height: 24,
                    width: 24,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <IconLinearDollarCircle size={16} />
                </Box>
              }
              endIcon={<IconLinearArrowDown size={16} />}
              sx={{
                borderRadius: 2,
                background: theme.colors?.primary.parchment,
              }}
            >
              <Typography variant="subhead-xl">
                {selectedSource.length > 0
                  ? selectedSource.join(' & ')
                  : 'Paid & Organic'}
              </Typography>
            </Button>
          )}
          options={Object.values(SocialPostMentionSource).map((source) => ({
            onClick: () => {},
            closeOnClick: false,
            renderCustomComponent: () => {
              return (
                <CheckboxMenuItem
                  value={source}
                  label={source}
                  checked={selectedSource.includes(source)}
                  isCheckboxExclusiveClick={false}
                  onClick={() => onSelectSource(source)}
                  sx={{
                    mb: 0.5,
                    span: {
                      ...theme.typography['subhead-lg'],
                      color: theme.colors?.primary.black,
                    },
                  }}
                />
              );
            },
          }))}
          PaperProps={{
            style: {
              padding: theme.spacing(2),
              background: theme.colors?.primary.parchment,
            },
          }}
        />
      </CreatePortalById>
      <Box
        sx={{
          p: 6,
          borderRadius: 6,
          border: `1px solid ${theme.colors?.utility[300]}`,
          background: theme.colors?.primary.parchment,
          boxShadow: '0px 2px 10px -3px rgba(0, 0, 0, 0.05)',
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="headline-xl">
            {brandMentionData?.totalCount ?? 0}
          </Typography>
          <Typography
            variant="headline-lg"
            color={theme.colors?.utility['green-3']}
          >
            ↑
            {formatBigNumber(brandMentionData?.previousDurationTotalCount ?? 0)}{' '}
            {brandMentionData?.totalCount
              ? `(${(
                  (brandMentionData.previousDurationTotalCount /
                    brandMentionData.totalCount) *
                  100
                ).toFixed(2)}%) vs last month`
              : 'N/A'}
          </Typography>
        </Box>
        <ResponsiveContainer width="100%" height={350}>
          <AreaChart
            data={hasData ? brandMentionData.data : STATIC_POINTS}
            margin={{
              top: 10,
              right: 30,
              left: 0,
              bottom: 0,
            }}
            onClick={onClickChartPoint}
          >
            {hasData && (
              <CartesianGrid
                vertical={false}
                horizontal
                stroke={theme.colors?.utility[400]}
                strokeDasharray="4 4"
              />
            )}
            <XAxis
              dataKey="date"
              tickFormatter={(value) => moment(value).format('MMM DD')}
              axisLine={false}
              tickLine={false}
              stroke={theme.colors?.utility[600]}
              fontSize={theme.typography['headline-sm'].fontSize}
              fontWeight={theme.typography['headline-sm'].fontWeight}
              fontFamily={theme.typography['headline-sm'].fontFamily}
              padding={{ left: 30, right: 30 }}
            />
            <YAxis
              dataKey="brandMentionCount"
              tickFormatter={(value) => formatBigNumber(value)}
              axisLine={false}
              tickLine={false}
              stroke={theme.colors?.utility[600]}
              fontSize={theme.typography['headline-sm'].fontSize}
              fontWeight={theme.typography['headline-sm'].fontWeight}
              fontFamily={theme.typography['headline-sm'].fontFamily}
              {...(!hasData && {
                ticks: Y_AXIS_TICKS,
                domain: [0, 400],
                allowDataOverflow: true,
              })}
            />
            {/** Range on y axis - For showing All time high */}
            {hasData && <Tooltip content={createCustomTooltipContent()} />}
            {hasData && (
              <Area
                type="monotone"
                dataKey="brandMentionCount"
                stroke={theme.colors?.utility[600]}
                strokeWidth={3}
                fill="rgba(224, 224, 224, 0.50)"
                style={{ cursor: 'pointer' }}
              />
            )}
          </AreaChart>
        </ResponsiveContainer>

        {!hasData && (
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              textAlign: 'center',
              background: 'rgba(35, 6, 3, 0.03)',
              borderRadius: 3,
              p: 6,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 3,
              minWidth: 350,
            }}
          >
            <Typography variant="headline-lg">😶</Typography>
            <Typography
              variant="headline-sm"
              color={theme.colors?.utility[700]}
            >
              {loading
                ? 'Loading...'
                : 'No brand mentions found for the selected period.'}
            </Typography>
          </Box>
        )}
      </Box>
    </>
  );
};
